/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Slider from 'components/Slider/Slider';
import useBreakpoints from 'utils/breakpoints';
import Arrow from 'components/Icons/Arrow/Arrow';
import Property from 'components/Property/Property';

import './PropertySlider.scss';

/**
 * This is the PropertySlider component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function PropertySlider({
  heading,
  navigation,
  prev,
  next,
  properties,
  total,
  index,
}) {
  const breakpoint = useBreakpoints({
    tablet: 760,
    desktop: 1024,
  });
  const container = useRef(null);
  const slider = useRef(null);

  const [active, setActive] = useState(false);

  function getSettings() {
    switch (breakpoint) {
      case 'desktop':
        return {
          show: 4,
          slide: 4,
        };
      case 'tablet':
        return {
          show: 2,
          slide: 2,
        };
      default:
        return {};
    }
  }

  function renderIndexButton() {
    if (!index) return null;

    return (
      <a className="button yellow index-button animate-2" href={index.url}>
        {index.text}
      </a>
    );
  }

  function renderTotal() {
    if (total === null) return null;
    return <em>{total}</em>;
  }

  return (
    <div ref={container} className="gallery property-slider" slider-stagger="">
      <div className="page-padding filter">
        <h2 className="animate-1">
          {renderTotal()}
          {heading}
        </h2>
        {renderIndexButton()}
        <nav
          className={classnames('navigation', { active })}
          title={navigation}
        >
          <button
            className="animate-3"
            onClick={() => {
              slider.current.prev();
            }}
          >
            <Arrow left title={prev} />
          </button>
          <button
            className="animate-3"
            onClick={() => {
              slider.current.next();
            }}
          >
            <Arrow right title={next} />
          </button>
        </nav>
      </div>
      <Slider
        ref={slider}
        className="animate-3"
        {...getSettings()}
        onActive={setActive}
      >
        {properties.map(({ id, ...props }, index) => (
          <Property
            key={id}
            className={`mod-${index % 8}`}
            {...props}
            priority={3}
          />
        ))}
      </Slider>
      <div className="page-padding buttons">{renderIndexButton()}</div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
PropertySlider.propTypes = {
  heading: PropTypes.string,
  navigation: PropTypes.string,
  prev: PropTypes.string,
  next: PropTypes.string,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  total: PropTypes.string,
  index: PropTypes.shape({
    url: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
PropertySlider.defaultProps = {
  navigation: 'Navigate between properties',
  prev: 'Previous',
  next: 'Next',
  properties: [],
  total: null,
  index: null,
};

export default PropertySlider;
